import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { QrCode } from 'lucide-react'
import { Waypoint } from 'react-waypoint'

import { useQueryClient } from '@tanstack/react-query'
import {
    DRIVER_CARDS_PAGE_SIZE,
    buildAvailableCards,
} from './-available-orders-cards'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FilterField } from '@/components/common/FilterField'
import {
    orderKeys,
    useInfiniteSalesOrders,
} from '@/hooks/use-sales-order-query'
import {
    cdoKeys,
    useInfiniteContractDeliveries,
} from '@/hooks/use-contract-delivery-query'
import { useDebounced } from '@/hooks/use-debounced'
import { QRScannerDialog } from '@/components/sections/qr-scanner-dialog'
import { AvailableOrderCard } from '@/components/driver/available-order-card'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/driver/available-orders',
)({
    component: DriverAvailableOrdersPage,
})

export function DriverAvailableOrdersPage() {
    const [qrScanOpen, setQrScanOpen] = useState(false)
    const qc = useQueryClient()

    // Pool đơn chờ nhận có thể tới hàng nghìn đơn (~1.600 trên prod). Tải +
    // dựng theo trang: mỗi nguồn chỉ lấy DRIVER_CARDS_PAGE_SIZE đơn/lần, "Xem
    // thêm" mới gọi trang kế; tìm kiếm chạy ở server (FD-396). Trước đây tải hết
    // mọi trang tuần tự rồi mới hiện — 16 request nối đuôi.
    const [keyword, setKeyword] = useState('')
    const search = useDebounced(keyword.trim(), 300)
    const searchParams = search ? { search, search_address: '1' as const } : {}

    const sales = useInfiniteSalesOrders(
        {
            status: 'stock_out',
            // Đơn KiotViet là bán khách lẻ tại quầy, KHÔNG cần giao → loại khỏi
            // pool tài xế ngay ở BE (dù đơn ở stock_out như đơn giao).
            exclude_source_channel: 'kiotviet',
            // Đơn "Bán ngay" POS: khách đã cầm hàng về tại quầy, không đi giao.
            exclude_quick_sale: '1',
            ...searchParams,
        },
        DRIVER_CARDS_PAGE_SIZE,
    )
    const contracts = useInfiniteContractDeliveries(
        { status: 'confirmed', ...searchParams },
        DRIVER_CARDS_PAGE_SIZE,
    )

    const isLoading = sales.isPending || contracts.isPending
    const contractsHasMore = Boolean(contracts.hasNextPage)
    const cards = useMemo(
        () =>
            buildAvailableCards({
                contracts: contracts.data?.pages.flatMap((p) => p.items) ?? [],
                contractsHasMore,
                sales: sales.data?.pages.flatMap((p) => p.items) ?? [],
            }),
        [contracts.data, contractsHasMore, sales.data],
    )
    // Tổng lấy từ server (meta.total của trang đầu), không phải số đã tải.
    const total =
        (sales.data?.pages[0]?.meta?.total ?? 0) +
        (contracts.data?.pages[0]?.meta?.total ?? 0)
    const hasMore = contractsHasMore || Boolean(sales.hasNextPage)
    const loadingMore = sales.isFetchingNextPage || contracts.isFetchingNextPage
    // Đơn giao hợp đồng đứng đầu: tải hết đơn hợp đồng rồi mới tới đơn bán.
    const loadMore = () => {
        if (loadingMore) return
        if (contractsHasMore) void contracts.fetchNextPage()
        else if (sales.hasNextPage) void sales.fetchNextPage()
    }
    const handlePickupComplete = () => {
        void qc.invalidateQueries({ queryKey: orderKeys.lists() })
        void qc.invalidateQueries({ queryKey: cdoKeys.lists() })
    }

    return (
        <div className="space-y-4 page-pad">
            <div className="flex items-center justify-between">
                <div className="flex min-w-0 items-start gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <div>
                        <h1 className="text-xl font-semibold">
                            {m.driver_waiting_orders()}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {m.driver_order_subtitle()}
                        </p>
                    </div>
                </div>
                <Button onClick={() => setQrScanOpen(true)}>
                    <QrCode className="size-4" />
                    {m.driver_order_pickup_qr()}
                </Button>
            </div>

            {isLoading ? null : (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <FilterField
                        label={m.driver_search_label()}
                        className="sm:max-w-sm sm:flex-1"
                    >
                        <Input
                            placeholder={m.driver_search_placeholder()}
                            className="h-11 md:h-9"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                        />
                    </FilterField>
                    {cards.length > 0 ? (
                        <p className="text-muted-foreground text-sm tabular-nums">
                            {m.driver_showing_count({
                                shown: String(cards.length),
                                total: String(Math.max(total, cards.length)),
                            })}
                        </p>
                    ) : null}
                </div>
            )}

            {isLoading ? (
                <p className="text-sm text-muted-foreground">
                    {m.common_loading()}
                </p>
            ) : cards.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                    {keyword.trim()
                        ? m.driver_no_match()
                        : m.driver_no_orders()}
                </p>
            ) : (
                <>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {cards.map((card) => (
                            <AvailableOrderCard
                                key={`${card.kind}-${card.id}`}
                                card={card}
                            />
                        ))}
                    </div>
                    {/* Mốc cuộn: cuộn tới gần đáy (cách 400px) là tự tải trang
                        kế. Waypoint tự tìm khung cuộn cha (ở đây là <main>).
                        key theo số card: tải xong mà mốc vẫn trong tầm nhìn
                        (màn cao, ít đơn) thì mốc mới lại báo onEnter để tải
                        tiếp cho đầy màn hình. */}
                    {hasMore ? (
                        <Waypoint
                            key={cards.length}
                            onEnter={loadMore}
                            bottomOffset="-400px"
                        />
                    ) : null}
                    {loadingMore ? (
                        <p className="py-3 text-center text-sm text-muted-foreground">
                            {m.common_loading()}
                        </p>
                    ) : null}
                </>
            )}

            <QRScannerDialog
                open={qrScanOpen}
                onOpenChange={setQrScanOpen}
                onComplete={handlePickupComplete}
            />
        </div>
    )
}
